import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Modal, Button } from "antd";
import newLogo from "../../assets/images/logonew.jpg";
import "./VendorProfile.css";

/* This page sits outside Layout on purpose: Layout's navbar is built for a
   signed-in user and links to My Team / Products / Subscription, which a
   visitor arriving from a shared link cannot open. But a public page still
   needs to say whose site it is and offer a way in, so it carries its own
   minimal header instead of going bare. */
function PublicHeader() {
  /* Search needs a token — ResultsPage bails with `if (!token) return`, so
     sending a signed-out visitor to /home lands them on a search box that
     silently does nothing. Offer them the sign-in page instead, and say so
     on the label rather than promising a search they cannot run. */
  const signedIn = Boolean(localStorage.getItem("userToken"));

  return (
    <header className="vprofile-topbar">
      <div className="vprofile-topbar-inner">
        <Link className="vprofile-brand" to={signedIn ? "/home" : "/"}>
          <img src={newLogo} alt="Tooth Finder" />
        </Link>
        <div className="vprofile-topbar-right">
          <span className="vprofile-topbar-note">
            {signedIn ? "Signed in" : "Sign in to search moulds"}
          </span>
          <Link className="vprofile-topbar-action" to={signedIn ? "/home" : "/"}>
            {signedIn ? "Find a mould" : "Sign in"}
          </Link>
        </div>
      </div>
    </header>
  );
}

/* Drawn here rather than pulled from an icon pack: a single thin chevron
   with rounded caps, matching the weight of the page's own type. */
function Chevron({ direction }) {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <polyline
        points={direction === "left" ? "10,3 5,8 10,13" : "6,3 11,8 6,13"}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
      <path
        d="M9.2 1.5H4.3a1.3 1.3 0 0 0-1.3 1.3v10.4a1.3 1.3 0 0 0 1.3 1.3h7.4a1.3 1.3 0 0 0 1.3-1.3V5.2L9.2 1.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M9.1 1.6v3.7h3.7" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  );
}

/* Drawn in the same thin-stroke language as the chevron and the document
   icon, so the page's marks read as one set rather than three sources. */
function NoPhotoMark() {
  return (
    <svg viewBox="0 0 40 32" width="40" height="32" aria-hidden="true" focusable="false">
      <rect
        x="1" y="1" width="38" height="30" rx="3"
        fill="none" stroke="currentColor" strokeWidth="1.2"
      />
      <path
        d="M6 24l8.5-9 5.5 6 4.5-4.5L34 24"
        fill="none" stroke="currentColor" strokeWidth="1.2"
        strokeLinecap="round" strokeLinejoin="round"
      />
      <circle cx="14" cy="10.5" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
      <rect
        x="3" y="7" width="10" height="7" rx="1.6"
        fill="none" stroke="currentColor" strokeWidth="1.3"
      />
      <path
        d="M5.6 7V5.4a2.4 2.4 0 0 1 4.8 0V7"
        fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
      <rect x="2.5" y="4.5" width="15" height="11" rx="1.8" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="m3.2 5.6 6.8 5 6.8-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        d="M6.2 3.3 8 3.9l.8 2.6-1.4 1.2a9 9 0 0 0 3.9 3.9l1.2-1.4 2.6.8.6 1.8a1.4 1.4 0 0 1-1.3 1.9A11.6 11.6 0 0 1 3.4 4.6a1.4 1.4 0 0 1 1.9-1.3Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* The outward arrow on a contact link, drawn rather than set as a glyph so
   it carries the same stroke weight as the icons beside it. */
function OutArrow() {
  return (
    <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true" focusable="false">
      <path
        d="M3.4 8.6 8.6 3.4M4.4 3.4h4.2v4.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* Builds the attribute line from whatever the record actually has. Most
   moulds carry a code, a colour and a shape; dimensions and mass are
   frequently absent, so the pieces are filtered rather than assumed - an
   unconditional template renders "· ·" on a sparse record. */
function mouldMeta(p) {
  const traits = [p.shape, p.case].filter(Boolean);
  const size =
    p.width && p.height ? `${p.width} × ${p.height} mm` : p.width || p.height || "";
  const mass = p.mass ? `${p.mass} g` : "";
  const measures = [size, mass].filter(Boolean);
  return { traits, measures };
}

export default function VendorProfile() {
  const { slug } = useParams();
  const [state, setState] = useState({ status: "loading", data: null });
  const trackRef = useRef(null);
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [query, setQuery] = useState("");
  const [viewingDoc, setViewingDoc] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [facets, setFacets] = useState({});
  const [copied, setCopied] = useState("");

  /* Read before the early returns below: hooks must run unconditionally. */
  const imageCount = state.data?.vendor?.images?.length || 0;

  /* Derive the active slide from the track's real scroll position, so
     dragging or flicking the images stays in step with the dots. */
  const handleTrackScroll = () => {
    const track = trackRef.current;
    if (!track || !track.clientWidth) return;
    setSlide(Math.round(track.scrollLeft / track.clientWidth));
  };

  const goToSlide = (index) => {
    const track = trackRef.current;
    if (!track || !imageCount) return;
    /* Wraps, so the arrows never dead-end and the auto-advance loops. */
    const target = (index + imageCount) % imageCount;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    track.scrollTo({
      left: target * track.clientWidth,
      behavior: reduced ? "auto" : "smooth",
    });
  };

  /* Auto-advance. Pauses on hover and on keyboard focus so it cannot pull
     the image out from under someone reading or tabbing through it, and
     stays still entirely for anyone who asked for reduced motion. */
  useEffect(() => {
    if (imageCount <= 1 || paused) return undefined;
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }

    const id = setInterval(() => {
      const track = trackRef.current;
      if (!track || !track.clientWidth) return;
      const current = Math.round(track.scrollLeft / track.clientWidth);
      const next = (current + 1) % imageCount;
      track.scrollTo({ left: next * track.clientWidth, behavior: "smooth" });
    }, 4500);

    return () => clearInterval(id);
  }, [imageCount, paused]);

  /* A shared link's tab, bookmark and history entry all read from the title.
     Every vendor page previously said "DMCA Dent", so ten open tabs were
     indistinguishable. Restored on unmount so other pages keep theirs.

     Note this cannot fix social link previews: create-react-app ships one
     static index.html and WhatsApp / Slack / Facebook do not run JavaScript,
     so og: tags written here are never seen by their crawlers. That needs
     prerendering or server rendering, which is a separate piece of work. */
  useEffect(() => {
    const name = state.data?.vendor?.name;
    if (!name) return undefined;
    const previous = document.title;
    document.title = `${name} | Tooth Finder`;
    return () => {
      document.title = previous;
    };
  }, [state.data]);

  useEffect(() => {
    let live = true;
    const url = process.env.REACT_APP_API_BASE_URL;

    /* Sent when present, absent otherwise - the endpoint is public either
       way. It only decides whether the document download URLs come back
       (see optionalAuth on the route). */
    const token = localStorage.getItem("userToken");

    axios
      .get(`${url}/open/v1/public-vendors/${encodeURIComponent(slug)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        if (!live) return;
        if (!res.data || !res.data.vendor) {
          setState({ status: "error", data: null });
          return;
        }
        setState({ status: "ready", data: res.data });
      })
      .catch((error) => {
        if (!live) return;
        setState({
          status: error.response?.status === 404 ? "missing" : "error",
          data: null,
        });
      });

    return () => {
      live = false;
    };
  }, [slug]);

  /* Copying an address is the commonest thing a buyer does with it, and
     selecting text off a page on a phone is genuinely fiddly. Falls back to
     a hidden textarea where the async clipboard API is unavailable, which
     includes any non-HTTPS origin. */
  const copy = async (value, key) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const el = document.createElement("textarea");
        el.value = value;
        el.setAttribute("readonly", "");
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? "" : c)), 1800);
    } catch {
      /* Clipboard denied. The value is still visible and selectable, so
         there is nothing useful to announce. */
    }
  };

  if (state.status === "loading") {
    return (
      <>
        <PublicHeader />
        <div className="vprofile">
          {/* Mirrors the real layout - a text column beside the media panel,
              then the grid - so the page does not jump when data lands. */}
          <div className="vprofile-skeleton-hero">
            <div>
              <div className="vprofile-skeleton-line vprofile-skeleton-eyebrow" />
              <div className="vprofile-skeleton-line vprofile-skeleton-title" />
              <div className="vprofile-skeleton-line vprofile-skeleton-sub" />
              <div className="vprofile-skeleton-line vprofile-skeleton-sub" />
            </div>
            <div className="vprofile-skeleton-media" />
          </div>
          <div className="vprofile-skeleton-grid">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="vprofile-skeleton-card" />
            ))}
          </div>
        </div>
      </>
    );
  }

  if (state.status === "missing" || state.status === "error") {
    return (
      <>
        <PublicHeader />
        <div className="vprofile vprofile-missing">
          <h1 className="vprofile-missing-title">
            {state.status === "missing"
              ? "No lab at this address"
              : "This page could not load"}
          </h1>
          <p className="vprofile-missing-text">
            {state.status === "missing"
              ? "The link may be out of date, or the lab may have been removed."
              : "Something went wrong fetching this lab. Try again shortly."}
          </p>
          <Link className="vprofile-missing-link" to="/home">
            Search moulds
          </Link>
        </div>
      </>
    );
  }

  const { vendor, isClaimed, isActiveListing, totalProducts } = state.data;
  const products = state.data.products || [];
  const images = vendor.images || [];
  const documents = vendor.documents || [];
  const hasContact = Boolean(vendor.contactEmail || vendor.contactPhone);

  /* Matches the fields a buyer would actually type: the mould's name, its
     codes, and the attributes they were filtering on in search. */
  const searching = query.trim().length > 0;
  const needle = query.trim().toLowerCase();

  /* Facets are derived from the catalogue in hand, never hardcoded: these
     are free-text fields a vendor typed, so the only honest option list is
     the one the data actually contains.

     A facet with a single distinct value is dropped - a lone "Upper" toggle
     filters nothing and only adds furniture. That means small catalogues
     show no filters at all, which is correct: at six moulds the eye is
     faster than a control. */
  const FACET_KEYS = [
    { key: "case", label: "Case" },
    { key: "shape", label: "Shape" },
    { key: "color", label: "Colour" },
  ];
  const facetGroups = FACET_KEYS.map(({ key, label }) => ({
    key,
    label,
    values: [...new Set(products.map((p) => p[key]).filter(Boolean))].sort(),
  })).filter((g) => g.values.length > 1);

  const activeFacets = Object.entries(facets).filter(([, v]) => v);

  const shown = products.filter((p) => {
    const matchesFacets = activeFacets.every(([k, v]) => p[k] === v);
    if (!matchesFacets) return false;
    if (!searching) return true;
    return [p.name, p.alphaCode, p.mouldNumber, p.shape, p.color, p.case, p.description]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(needle));
  });

  const filtering = searching || activeFacets.length > 0;

  /* Long descriptions are collapsed on small screens so the catalogue - the
     thing that actually persuades - is not pushed below several screenfuls
     of prose. The full text is always in the DOM; only its height is
     clipped, so nothing is hidden from search engines or screen readers. */
  const about = vendor.description || "";
  const longAbout = about.length > 320;

  const contactRows = [
    vendor.contactEmail && {
      key: "email",
      label: "Email",
      value: vendor.contactEmail,
      href: `mailto:${vendor.contactEmail}`,
      icon: <MailIcon />,
    },
    vendor.contactPhone && {
      key: "phone",
      label: "Call",
      value: vendor.contactPhone,
      href: `tel:${vendor.contactPhone}`,
      icon: <PhoneIcon />,
    },
  ].filter(Boolean);

  return (
    <>
      <span className="vprofile-grain" aria-hidden="true" />
      <PublicHeader />

      <div className="vprofile">
        {/*
          Hero: the lab's identity, its own words and the way to reach it in
          one column, the photographs in the other. Contact is the whole
          conversion on this page - there is no cart and no enquiry form - so
          it sits at eye level rather than at the foot of the page.
        */}
        <section
          className={`vprofile-hero${
            images.length === 0 ? " vprofile-hero-solo" : ""
          }`}
        >
          <div className="vprofile-hero-main">
            {/* The eyebrow carries the lab's own category. It deliberately
                does NOT say "Verified Vendor" the way the reference mock
                did: there is no verification field on this record, and a
                badge claiming one would be inventing trust we cannot back. */}
            <span className="vprofile-eyebrow">
              {vendor.category || "Dental laboratory"}
            </span>

            <h1 className="vprofile-name">{vendor.name}</h1>

            {isActiveListing && (
              <p className="vprofile-count">
                {totalProducts} {totalProducts === 1 ? "mould" : "moulds"} on
                record
              </p>
            )}

            {!isClaimed && (
              <p className="vprofile-unclaimed">
                This lab has not claimed its listing.{" "}
                <Link to="/vendors">Is this your lab?</Link>
              </p>
            )}

            {about && (
              <>
                <div
                  className={`vprofile-about${
                    longAbout && !aboutOpen ? " vprofile-about-clamped" : ""
                  }`}
                >
                  {about.split(/\n{2,}/).map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
                {longAbout && (
                  <button
                    type="button"
                    className="vprofile-about-toggle"
                    onClick={() => setAboutOpen((v) => !v)}
                  >
                    {aboutOpen ? "Show less" : "Read more"}
                  </button>
                )}
              </>
            )}

            {contactRows.length > 0 && (
              <div className="vprofile-contact">
                {contactRows.map((row) => (
                  <div className="vprofile-contact-item" key={row.key}>
                    <span className="vprofile-contact-icon">{row.icon}</span>
                    <span className="vprofile-contact-text">
                      <span className="vprofile-contact-label">{row.label}</span>
                      <a className="vprofile-contact-value" href={row.href}>
                        {row.value}
                        <OutArrow />
                      </a>
                    </span>
                    <button
                      type="button"
                      className="vprofile-copy"
                      onClick={() => copy(row.value, row.key)}
                    >
                      {copied === row.key ? "Copied" : "Copy"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {images.length > 0 && (
            <div
              className="vprofile-gallery"
              /* Arrow keys once the gallery has focus. The arrows are
                 already reachable by tab; this makes the whole strip
                 behave the way a carousel is expected to. */
              tabIndex={images.length > 1 ? 0 : -1}
              role={images.length > 1 ? "group" : undefined}
              aria-label={images.length > 1 ? "Laboratory photos" : undefined}
              onKeyDown={(e) => {
                if (images.length < 2) return;
                if (e.key === "ArrowRight") { e.preventDefault(); goToSlide(slide + 1); }
                if (e.key === "ArrowLeft") { e.preventDefault(); goToSlide(slide - 1); }
              }}
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onFocusCapture={() => setPaused(true)}
              onBlurCapture={() => setPaused(false)}
            >
              {/* A scroll-snap track rather than a JS-transformed carousel:
                  the images are in the DOM and scrollable even if the
                  controls never mount. */}
              <div
                className="vprofile-slides"
                ref={trackRef}
                onScroll={handleTrackScroll}
              >
                {images.map((src, i) => (
                  <img
                    key={src || i}
                    src={src}
                    alt={`${vendor.name} work ${i + 1}`}
                    loading="lazy"
                  />
                ))}
              </div>

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="vprofile-slider-btn vprofile-slider-prev"
                    onClick={() => goToSlide(slide - 1)}
                    aria-label="Previous image"
                  >
                    <Chevron direction="left" />
                  </button>
                  <button
                    type="button"
                    className="vprofile-slider-btn vprofile-slider-next"
                    onClick={() => goToSlide(slide + 1)}
                    aria-label="Next image"
                  >
                    <Chevron direction="right" />
                  </button>
                  <div className="vprofile-dots">
                    {images.map((src, i) => (
                      <button
                        key={src || i}
                        type="button"
                        className={`vprofile-dot${
                          i === slide ? " vprofile-dot-active" : ""
                        }`}
                        onClick={() => goToSlide(i)}
                        aria-label={`Go to image ${i + 1}`}
                        aria-current={i === slide ? "true" : undefined}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </section>

        {/* Only present while the listing is active - the endpoint omits the
            field entirely for a lapsed vendor. */}
        {documents.length > 0 && (
          <section className="vprofile-section">
            <h2 className="vprofile-h2">Documents</h2>
            {state.data.vendor.documentsRequireSignIn && (
              <p className="vprofile-doc-note">
                Sign in to download this lab&apos;s documents.
              </p>
            )}
            <ul className="vprofile-doc-grid">
              {documents.map((d) =>
                /* No URL means the server withheld it because nobody is
                   signed in. Rendered as a link to sign-in rather than a
                   dead row, so the visitor knows the document exists and
                   what to do about it. */
                d.url ? (
                  <li key={d.url}>
                    {/* Stays an anchor with a real href so right-click "save
                        link as", middle-click and copy-link all still work;
                        the click is intercepted to read it in place. */}
                    <a
                      className="vprofile-doc"
                      href={d.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        setViewingDoc(d);
                      }}
                    >
                      <span className="vprofile-doc-icon">
                        <DocIcon />
                      </span>
                      <span className="vprofile-doc-title">{d.title}</span>
                      <span className="vprofile-doc-type">PDF</span>
                    </a>
                  </li>
                ) : (
                  <li key={d.title}>
                    <Link className="vprofile-doc vprofile-doc-locked" to="/">
                      <span className="vprofile-doc-icon">
                        <LockIcon />
                      </span>
                      <span className="vprofile-doc-title">{d.title}</span>
                      <span className="vprofile-doc-type">Sign in</span>
                    </Link>
                  </li>
                )
              )}
            </ul>
          </section>
        )}

        <section className="vprofile-section">
          <div className="vprofile-moulds-head">
            <h2 className="vprofile-h2">
              {/* No count for a lapsed listing: the products are withheld,
                  not absent, so "0 moulds" would misstate the catalogue. */}
              {isActiveListing ? (
                <>
                  <span className="vprofile-count-figure">
                    {filtering ? shown.length : totalProducts}
                  </span>{" "}
                  {(filtering ? shown.length : totalProducts) === 1
                    ? "mould"
                    : "moulds"}{" "}
                  from this lab
                </>
              ) : (
                "Moulds from this lab"
              )}
            </h2>

            {products.length > 0 && (
              /* Filters the list already in hand rather than calling the
                 API: the whole catalogue for one lab is on the page. */
              <div className="vprofile-search">
                <input
                  type="search"
                  className="vprofile-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search this lab's moulds"
                  aria-label="Search this lab's moulds"
                />
                {filtering && (
                  <button
                    type="button"
                    className="vprofile-search-clear"
                    onClick={() => {
                      setQuery("");
                      setFacets({});
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>

          {facetGroups.length > 0 && (
            /* Toggles, not decorative chips: each one is a control that
               changes what is on screen. Single-select per group, because a
               buyer narrows by one case or one shape at a time. */
            <div className="vprofile-facets">
              {facetGroups.map((g) => (
                <div className="vprofile-facet" key={g.key}>
                  <span className="vprofile-facet-label">{g.label}</span>
                  {g.values.map((v) => {
                    const active = facets[g.key] === v;
                    return (
                      <button
                        key={v}
                        type="button"
                        className={`vprofile-facet-btn${
                          active ? " vprofile-facet-active" : ""
                        }`}
                        aria-pressed={active}
                        onClick={() =>
                          setFacets((f) => ({ ...f, [g.key]: active ? "" : v }))
                        }
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {shown.length ? (
            <ul className="vprofile-grid">
              {shown.map((p) => {
                const { traits, measures } = mouldMeta(p);
                const cover = Array.isArray(p.images) ? p.images[0] : p.images;
                return (
                  <li key={p._id}>
                    <Link className="vprofile-card" to={`/product-description/${p._id}`}>
                      {/* The reference mock put the lab's name in this bar.
                          On a single lab's own page that is the same string
                          on every card, so the code - the thing a buyer
                          actually quotes - goes here instead. */}
                      <span className="vprofile-card-head">
                        <span className="vprofile-card-dims">
                          {measures.length ? measures.join(" · ") : "No dimensions"}
                        </span>
                        {p.alphaCode && (
                          <span className="vprofile-card-code">{p.alphaCode}</span>
                        )}
                      </span>

                      <span className="vprofile-card-figure">
                        {cover ? (
                          <img src={cover} alt={p.name} loading="lazy" />
                        ) : (
                          <span className="vprofile-card-noimg">
                            <NoPhotoMark />
                            <span>No photo</span>
                          </span>
                        )}
                      </span>

                      <span className="vprofile-card-foot">
                        <span className="vprofile-card-name">{p.name}</span>
                        {p.color && (
                          <span className="vprofile-card-colour">{p.color}</span>
                        )}
                        {traits.length > 0 && (
                          <span className="vprofile-card-traits">
                            {traits.join(" · ")}
                          </span>
                        )}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : filtering ? (
            <p className="vprofile-empty">
              {searching
                ? `No moulds here match "${query.trim()}".`
                : "No moulds match those filters."}
            </p>
          ) : !isActiveListing ? (
            /* The endpoint returns no products for a lapsed listing, so
               "No moulds listed yet" would be a lie - the lab may well have
               a full catalogue. */
            <p className="vprofile-empty">
              This lab is not showing its moulds at the moment.
            </p>
          ) : (
            <p className="vprofile-empty">No moulds listed yet.</p>
          )}
        </section>
      </div>

      {/* The reference mock's footer carried Privacy Policy, Terms of
          Service, Laboratory Standards and Contact Support. None of those
          pages exist, and four links to nowhere are worse than none, so the
          footer carries only the two routes that are real. */}
      <footer className="vprofile-footer">
        <div className="vprofile-footer-inner">
          <span className="vprofile-footer-brand">Tooth Finder</span>
          <p className="vprofile-footer-note">
            © {new Date().getFullYear()} Tooth Finder. Moulds and details are
            supplied by the laboratory.
          </p>
          <nav className="vprofile-footer-links">
            <Link to="/home">Search moulds</Link>
            <Link to="/vendors">List your lab</Link>
          </nav>
        </div>
      </footer>

      {/* Contact stays reachable at every scroll depth on a phone, where the
          hero block has long since scrolled away. Getting in touch is the
          only conversion this page has. */}
      {hasContact && (
        <div className="vprofile-actionbar">
          {vendor.contactEmail && (
            <a className="vprofile-action" href={`mailto:${vendor.contactEmail}`}>
              <MailIcon />
              Email
            </a>
          )}
          {vendor.contactPhone && (
            <a className="vprofile-action" href={`tel:${vendor.contactPhone}`}>
              <PhoneIcon />
              Call
            </a>
          )}
        </div>
      )}

      <Modal
        title={viewingDoc?.title || "Document"}
        open={Boolean(viewingDoc)}
        onCancel={() => setViewingDoc(null)}
        centered
        width={900}
        destroyOnClose
        className="vprofile-doc-viewer"
        footer={[
          /* Not every browser renders a PDF inline - iOS Safari refuses
             inside an iframe - so there is always a way to the file. */
          <Button
            key="open"
            href={viewingDoc?.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in new tab
          </Button>,
          <Button key="close" type="primary" onClick={() => setViewingDoc(null)}>
            Close
          </Button>,
        ]}
      >
        {viewingDoc && (
          <iframe
            className="vprofile-doc-frame"
            src={viewingDoc.url}
            title={viewingDoc.title || "Document"}
          />
        )}
      </Modal>
    </>
  );
}
